import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { useEnforcement } from '@/hooks/useEnforcement';
import { useSubscription } from '@/hooks/useSubscription';
import { allowedMcTotal } from '@/lib/mcLimitMarker';

interface McLimitNoticeProps {
  /** Anzahl der vom Server geblockten Merchant Center (`mc_limit_blocked:<n>`). */
  blockedCount: number;
  className?: string;
}

/**
 * Lokalisierte Hinweiszeile fuer das MC-Anzahl-Gate (Preisumbau Baender):
 * `fetch-account-data` hat NEUE Merchant Center ueber der Tarif-Grenze nicht
 * verbunden und den Marker `mc_limit_blocked:<n>` in `sync_message`
 * hinterlassen. Gerendert im SyncProgressDialog (statt des rohen Markers) und
 * in den Google-Account-Karten (solange der letzte Sync den Marker traegt).
 *
 * Die erlaubte Gesamtzahl kommt aus derselben Rechnung wie serverseitig:
 * Inklusivmenge nach Bandgroesse + gebuchte MC-Add-ons (`allowedMcTotal`).
 * Ist sie unbegrenzt (sollte mit Marker nie vorkommen), faellt die Zeile auf
 * die Fassung ohne Zahl zurueck statt "Infinity" zu drucken.
 */
const McLimitNotice: React.FC<McLimitNoticeProps> = ({ blockedCount, className }) => {
  const { t } = useTranslation('settings');
  const { limit } = useEnforcement();
  const { subscription } = useSubscription();

  const allowed = allowedMcTotal(limit, subscription?.addon_mc_booked);

  return (
    <div
      className={`rounded-lg border border-warning/40 bg-warning-light/40 p-3 text-xs text-foreground ${className ?? ''}`}
      role="status"
    >
      <p className="flex items-start gap-1.5">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-warning-ink" aria-hidden="true" />
        <span>
          {Number.isFinite(allowed)
            ? t('mcLimit.blocked', { count: blockedCount, allowed })
            : t('mcLimit.blockedNoAllowance', { count: blockedCount })}{' '}
          <Link
            to="/settings?tab=subscription"
            className="font-medium text-primary underline-offset-2 hover:underline"
          >
            {t('mcLimit.cta')}
          </Link>
        </span>
      </p>
    </div>
  );
};

export default McLimitNotice;
